import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2, Clock, Link2Off, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BRAND } from "@/brand/brand";
import { cn } from "@/lib/utils";
import { ButtonTip, TipProvider } from "@/components/ui/button-tip";
import { EASE } from "./calm";
import { COMPANY_NAME, LEGAL_LINKS } from "./legalLinks";
import type { PageProblem, SaveStatus } from "./useOnboardingController";

/** A language is named in its own language, whatever the page speaks right now. */
const LANGUAGE_NAME = { de: "Deutsch", en: "English" } as const;

export function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const current = i18n.language?.startsWith("en") ? "en" : "de";
  const choose = (language: "de" | "en") => {
    void i18n.changeLanguage(language);
    try {
      localStorage.setItem("i18nextLng", language);
    } catch {
      // storage unavailable (private window): the choice just does not survive a reload
    }
  };
  return (
    <div role="group" aria-label={t("onboardingPage.language")} className="flex rounded-full border border-border p-0.5 text-xs">
      {(["de", "en"] as const).map((language) => (
        <ButtonTip key={language} label={t(`onboardingPage.tips.language.${language}`)} side="bottom">
        <button
          type="button"
          onClick={() => choose(language)}
          aria-pressed={current === language}
          aria-label={LANGUAGE_NAME[language]}
          lang={language}
          className={cn(
            "rounded-full px-2.5 py-1 font-medium uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            current === language ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {language}
        </button>
        </ButtonTip>
      ))}
    </div>
  );
}

export function PageFrame({
  status,
  progress,
  children,
}: {
  status?: ReactNode;
  progress?: ReactNode;
  children: ReactNode;
}) {
  // `overflow-x: clip` (not `hidden`, which would end the header's stickiness) keeps slide-ins from
  // ever producing a horizontal scrollbar.
  return (
    <TipProvider>
    <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground" style={{ overflowX: "clip" }}>
      <div className="onb-ground" aria-hidden="true" />
      <header className="sticky top-0 z-20 border-b border-border bg-background/85 pt-[env(safe-area-inset-top)] backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1120px] items-center justify-between gap-3 px-4">
          {/* logo and brand name are one element; the name stands beside the logo, so the logo itself says nothing (no double reading) */}
          <div className="flex min-w-0 shrink items-center gap-[11px]" data-testid="brand">
            <img src={BRAND.logo} alt="" className="h-7 w-auto max-w-[150px] shrink-0 object-contain object-left" />
            {/* under 380 px the name steps aside for the save status and the language switch - but stays for a screen reader */}
            <span className="whitespace-nowrap text-[15px] font-semibold tracking-tight text-foreground sm:text-base max-[379px]:sr-only" data-testid="brand-name">
              {BRAND.name}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {status}
            <LanguageToggle />
          </div>
        </div>
        {progress && <div className="pb-2">{progress}</div>}
      </header>
      <main className="relative z-10 mx-auto w-full max-w-[1120px] flex-1 px-4 pb-12 pt-6 sm:pt-10">{children}</main>
      <LegalFooter />
    </div>
    </TipProvider>
  );
}

/** Imprint, privacy and the company, at the very end of every view: in the flow of the page, never over it. */
export function LegalFooter() {
  const { t } = useTranslation();
  const link = "relative rounded-sm text-muted-foreground no-underline hover:text-foreground focus-visible:text-foreground bg-[linear-gradient(currentColor,currentColor)] bg-[length:0%_1px] bg-left-bottom bg-no-repeat pb-0.5 transition-[background-size] duration-500 hover:bg-[length:100%_1px] focus-visible:bg-[length:100%_1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none";
  return (
    <footer className="relative z-10 bg-background/80 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-xs text-muted-foreground" data-testid="legal-footer">
      <div className="mx-auto flex max-w-[1120px] flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4">
        <nav aria-label={t("onboardingPage.footer.label")} className="flex flex-wrap gap-x-5 gap-y-2">
          <a href={LEGAL_LINKS.imprint} target="_blank" rel="noopener noreferrer" className={link}>
            {t("onboardingPage.footer.imprint")}
          </a>
          <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className={link}>
            {t("onboardingPage.footer.privacy")}
          </a>
        </nav>
        <p>© {COMPANY_NAME}</p>
      </div>
    </footer>
  );
}

export function ProblemPage({ problem, onReload }: { problem: PageProblem; onReload: () => void }) {
  const { t } = useTranslation();
  const Icon = problem === "rate_limited" ? Clock : problem === "error" ? AlertCircle : Link2Off;
  return (
    <PageFrame>
      <div className="onb-rise mx-auto mt-6 max-w-lg rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <Icon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
        </span>
        <h1 className="mt-4 text-xl font-semibold tracking-tight">{t(`onboardingPage.page.${problem}.title`)}</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{t(`onboardingPage.page.${problem}.text`)}</p>
        {(problem === "rate_limited" || problem === "error") && (
          <ButtonTip label={t("onboardingPage.tips.reload")}>
            <Button className="mt-6" variant="outline" onClick={onReload}>
              {t("onboardingPage.page.reload")}
            </Button>
          </ButtonTip>
        )}
      </div>
    </PageFrame>
  );
}

/** What the first GET shows instead of a white flash: the frame and the shape of the page. */
export function LoadingPage() {
  const { t } = useTranslation();
  const bar = "motion-reduce:animate-none";
  return (
    <PageFrame>
      <div role="status" aria-busy="true" className="onb-fade space-y-6">
        <span className="sr-only">{t("onboardingPage.loading")}</span>
        <div className="space-y-3">
          <Skeleton className={cn("h-9 w-3/4", bar)} />
          <Skeleton className={cn("h-5 w-full", bar)} />
          <Skeleton className={cn("h-5 w-2/3", bar)} />
          <Skeleton className={cn("h-6 w-48 rounded-full", bar)} />
        </div>
        <Skeleton className={cn("h-20 w-full rounded-2xl", bar)} />
        <Skeleton className={cn("h-96 w-full rounded-2xl", bar)} />
      </div>
    </PageFrame>
  );
}

/** Saved state in the header. All states sit in one grid cell, so the row keeps its size and they cross-fade. */
export function SaveIndicator({ status, onRetry }: { status: SaveStatus; onRetry: () => void }) {
  const { t } = useTranslation();
  const layer = (key: SaveStatus, className: string, content: ReactNode) => {
    const active = status === key;
    return (
      <span
        key={key}
        data-save-state={key}
        data-active={active ? "true" : "false"}
        aria-hidden={active ? undefined : true}
        className={cn("col-start-1 row-start-1 flex items-center justify-end gap-1.5 whitespace-nowrap", className)}
        style={{
          opacity: active ? 1 : 0,
          visibility: active ? "visible" : "hidden",
          transition: `opacity 260ms ${EASE}, visibility 0s linear ${active ? "0ms" : "260ms"}`,
        }}
      >
        {content}
      </span>
    );
  };
  return (
    <span role="status" aria-live="polite" className="inline-grid text-xs text-muted-foreground">
      {layer("idle", "max-sm:hidden", t("onboardingPage.save.idle"))}
      {layer("locked", "", t("onboardingPage.save.locked"))}
      {layer(
        "saving",
        "",
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {t("onboardingPage.save.saving")}
        </>,
      )}
      {layer(
        "saved",
        "",
        <>
          <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden="true" />
          {t("onboardingPage.save.saved")}
        </>,
      )}
      {layer(
        "error",
        "text-destructive",
        <>
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="max-sm:hidden">{t("onboardingPage.save.error")}</span>
          <ButtonTip label={t("onboardingPage.tips.saveRetry")} side="bottom">
            <button type="button" tabIndex={status === "error" ? 0 : -1} onClick={onRetry} className="font-medium underline underline-offset-2">
              {t("onboardingPage.save.retry")}
            </button>
          </ButtonTip>
        </>,
      )}
    </span>
  );
}

